import { Form, Select } from 'antd';
import { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { SCard } from 'app/components/StaycationUI';
import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { useRoomData } from 'app/components/pages/Rooms/Common/hooks/useRoomData';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useHotels } from 'app/hooks/data/hotels/useHotel';
import { useBedCategories } from 'app/hooks/data/rooms/useBedCategories';
import { useRoomCategories } from 'app/hooks/data/rooms/useRoomCategories';
import type { BedCategory, IHotel, Room, RoomCategory } from 'app/typings';
import type { Hotel } from 'app/typings/hotels/Hotel/Hotel';

import { PackageFormFields } from '../utils';

import './BasicInfo.scss';

const RULES = [{ required: true, message: 'Required' }];
const DAY_EXPERIENCE_ID = 50;

const { Option } = Select;

type FormFieldsProps = {
  roomCategories?: RoomCategory[];
  bedCategories?: BedCategory[];
};

const PackageFields = ({ roomCategories, bedCategories }: FormFieldsProps) => {
  const { isEditMode } = useContext(ViewEditModeContext);
  const form = Form.useFormInstance();
  const formValues = Form.useWatch<Room>([], form);

  if (!formValues) {
    return null;
  }

  return (
    <>
      {PackageFormFields(
        formValues,
        roomCategories,
        bedCategories,
        isEditMode
      ).map(
        ({ label, name, required, description, component, valuePropName }) => (
          <FormLineLayout
            key={name}
            label={label}
            isRequired={required}
            description={description}
          >
            <Form.Item
              name={name}
              rules={required ? RULES : undefined}
              valuePropName={valuePropName}
            >
              {component}
            </Form.Item>
          </FormLineLayout>
        )
      )}
    </>
  );
};

type CommonFormProps = {
  hotels: IHotel[];
  roomCategories: RoomCategory[];
  bedCategories: BedCategory[];
};

const CommonForm = ({
  hotels,
  roomCategories,
  bedCategories,
}: CommonFormProps) => {
  const { isEditMode } = useContext(ViewEditModeContext);
  const form = Form.useFormInstance();
  const formValues = Form.useWatch<Room>([], form);
  const { id: roomIdParam } = useParams<{ id: string }>();
  const { hotelIdFromSearch } = useRoomData();

  if (!formValues) {
    return null;
  }

  return (
    <>
      <FormLineLayout label="Hotel" isRequired>
        <Form.Item name="hotelId" rules={RULES}>
          {isEditMode ? (
            <Select
              placeholder="Select a hotel"
              showSearch
              optionFilterProp="children"
              disabled={!!roomIdParam || !!hotelIdFromSearch}
            >
              {hotels.map((hotel) => (
                <Option key={hotel.id} value={hotel.id}>
                  {hotel.name}
                </Option>
              ))}
            </Select>
          ) : (
            <>{hotels.find((h) => h.id === formValues['hotelId'])?.name}</>
          )}
        </Form.Item>
      </FormLineLayout>
      <PackageFields
        roomCategories={roomCategories}
        bedCategories={bedCategories}
      />
    </>
  );
};

type BasicInfoProps = {
  hotel?: Hotel;
};

export const BasicInfo = ({ hotel }: BasicInfoProps) => {
  const { data: hotels } = useHotels();
  const { data: roomCategories } = useRoomCategories();
  const { data: bedCategories } = useBedCategories();

  const memoizedRoomCategories = useMemo(
    () => roomCategories,
    [roomCategories]
  );
  const memoizedBedCategories = useMemo(() => bedCategories, [bedCategories]);

  const filteredRoomCategories = !hotel?.isLegacy
    ? memoizedRoomCategories?.filter(
        (category) => category.id !== DAY_EXPERIENCE_ID
      )
    : memoizedRoomCategories;

  if (!hotels || !filteredRoomCategories || !memoizedBedCategories) {
    return null;
  }

  return (
    <SCard title="Basic Information" className="room-form">
      <CommonForm
        hotels={hotels}
        roomCategories={filteredRoomCategories}
        bedCategories={memoizedBedCategories}
      />
    </SCard>
  );
};
